import { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { alpha } from '@mui/material/styles'
import Icon from 'components/Icon'
import Type from 'components/Type'
import TextButton from 'components/TextButton'
import Paper from 'components/Paper'
import CloudinaryUpload from 'components/CloudinaryUpload'
import StandardModal from 'components/CommonUI/StandardModal'
import { useAuth0 } from '@auth0/auth0-react'
import { SET_USER } from 'api/constants'
import { useBreakpoints, useHeaderNavigation, useRoles } from 'utils/hooks'
import { ReactComponent as IconEdit } from 'icons/Edit.svg'
import { IS_BOOKING, IS_PRODUCTION } from 'utils/constants'
import { DEV_RESET_USER, DEV_RESET_CORPORATE_CUSTOMER } from 'api/constants'
import { useAction } from 'utils/actions'

const ProfileImageEdit = () => {
  const dispatch = useDispatch()
  const user = useSelector(({ global }) => global.user)

  const handleUploadSuccess = data => {
    if (!IS_BOOKING) {
      dispatch({
        type: SET_USER,
        data: { 
          profile_image_url: data.info?.secure_url,
          public_id: data.info?.public_id,
        },
      })
    }
  }

  return (
    <CloudinaryUpload
      preset="DEFAULT"
      options="OPTIONS_PROFILE"
      onUploadSuccess={handleUploadSuccess}
    >
      <Box
        sx={{
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        <Avatar
          src={user?.profile_image_url}
          sx={{
            width: 100,
            height: 100,
          }}
        />
        {!IS_BOOKING && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: 'background.inverse.main',
              color: 'background.inverse.text',
              border: '2px solid',
              borderColor: 'background.paper.main',
              width: 30,
              height: 30,
              borderRadius: '30px',
              oveflow: 'hidden',
              display: 'inline-grid',
              placeItems: 'center',
              placeContent: 'center',
              'svg': {
                transition: 'inherit',
                color: 'inherit',
              },
            }}
          >
            <IconEdit />
          </Box>
        )}
      </Box>
    </CloudinaryUpload>
  )
}

const NavContent = () => {
  const dispatch = useDispatch()
  const { openModal, closeModal } = useAction()
  const navigate = useHeaderNavigation()
  const user = useSelector(({ global }) => global.user)
  const roles = useRoles()
  const { xsDown } = useBreakpoints()
  const {
    logout,
  } = useAuth0()

  const handleLogout = () => {
    logout({ returnTo: window.location.origin })
  }

  const handleResetUser = () => {
    openModal(
      <StandardModal
        icon="StopHand"
        title="You’re about to reset your user"
        body={`Are you sure you want to continue, ${user.first_name} ${user.last_name}?`}
        actionOne={(
          <span
            onClick={() => {
              dispatch({ type: DEV_RESET_USER })
              closeModal()
            }}
          >
            Reset my user
          </span>
        )}
        actionTwo="Maybe later"
        closeModalAction="two"
      />
    )
  }

  const handleResetCustomer = () => {
    openModal(
      <StandardModal
        icon="StopHand"
        title="You’re about to reset your customer"
        body={`Are you sure you want to continue, ${user.first_name} ${user.last_name}?`}
        actionOne={(
          <span
            onClick={() => {
              dispatch({ type: DEV_RESET_CORPORATE_CUSTOMER })
              closeModal()
            }}
          >
            Reset my user
          </span>
        )}
        actionTwo="Maybe later"
        closeModalAction="two"
      />
    )
  }

  return (
    <Stack 
      display="grid"
      gap={2}
      sx={{ 
        p: 2,
        width: {
          xx: 320,
          sm: 400,
        },
        maxHeight: {
          xx: 'auto',
          md: '80vh',
        },
        overflowY: 'auto',
      }}
    >
      <Stack
        display="grid"
        gap={1.5}
        justifyContent="center"
        justifyItems="center"
      >
        <ProfileImageEdit />
        <Stack
          gap={.5}
        >
          <Type
            variant="title"
            color="neutralCool_700"
          >
            Hi, {user.first_name}!
          </Type>
          {roles.hasAnyRole && xsDown && (
            <Type
              variant="bodySmallBold"
              color="primaryHue"
              px={1}
            >
              {roles.userRoleNames.join(' / ')}
            </Type>
          )}
        </Stack>
      </Stack>
      <Paper
        round="doubleExtra"
        variant="neutralCool_75"
      >
        <Stack
          pt={3}
          pb={2}
          gap={1}
        >
          <Type
            variant="subLabelMedium"
            color="neutralCool_700"
            px={2}
          >
            My Account
          </Type>
          <Stack
            px={1}
          >
            <Paper
              round="extra"
              variant="transparent"
              variantHover="neutralCool"
              selectable
              tabindex="0"
              role="button"
              onClick={() => navigate('/my-account')}
            >
              <Stack
                py={1}
                px={1}
              >
                <Type
                  variant="p"
                >
                  Account Settings
                </Type>
              </Stack>
            </Paper>
            {!IS_PRODUCTION && (
              <Paper
                round="extra"
                variant="transparent"
                variantHover="neutralCool"
                selectable
                tabindex="0"
                role="button"
                onClick={() => navigate('/settings')}
              >
                <Stack
                  py={1}
                  px={1}
                >
                  <Type
                    variant="p"
                  >
                    DEV User Settings
                  </Type>
                </Stack>
              </Paper>
            )}
            {!IS_PRODUCTION && (
              <Paper
                round="extra"
                variant="transparent"
                variantHover="neutralCool"
                selectable
                tabindex="0"
                role="button"
                onClick={handleResetUser}
              >
                <Stack
                  py={1}
                  px={1}
                >
                  <Type
                    variant="p"
                  >
                    DEV Reset User
                  </Type>
                </Stack>
              </Paper>
            )}
            {!IS_PRODUCTION && user.is_admin && (
              <Paper
                round="extra"
                variant="transparent"
                variantHover="neutralCool"
                selectable
                tabindex="0"
                role="button"
                onClick={handleResetCustomer}
              >
                <Stack
                  py={1}
                  px={1}
                >
                  <Type
                    variant="p"
                  >
                    DEV Reset Customer
                  </Type>
                </Stack>
              </Paper>
            )}
            {/* <Paper
              round="extra"
              variant="transparent"
              variantHover="neutralCool"
              selectable
            >
              <Stack
                py={1}
                px={1}
              >
                <Type
                  variant="p"
                >
                  Preferences
                </Type>
              </Stack>
            </Paper>
            <Paper
              round="extra"
              variant="transparent"
            >
              <Stack
                py={1}
                px={1}
                sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'space-between' })}
              >
                <Type
                  variant="p"
                >
                  Dark Mode
                </Type>
              </Stack>
            </Paper> */}
          </Stack>
        </Stack>
      </Paper>
      {(user.is_admin || user.is_manager) && (
        <Paper
          round="doubleExtra"
          variant="neutralCool_75"
        >
          <Stack
            pt={3}
            pb={2}
            gap={1}
          >
            <Type
              variant="subLabelMedium"
              color="primaryHue"
              px={2}
            >
              Admin Settings
            </Type>
            <Stack
              px={1}
            >
              {user.is_admin && (
                <>
                  <Paper
                    round="extra"
                    variant="transparent"
                    variantHover="neutralCool"
                    selectable
                    tabindex="0"
                    role="button"
                    onClick={() => navigate('/admin/account-details')}
                  >
                    <Stack
                      py={1}
                      px={1}
                    >
                      <Type
                        variant="p"
                      >
                        Account Details
                      </Type>
                    </Stack>
                  </Paper>
                  <Paper
                    round="extra"
                    variant="transparent"
                    variantHover="neutralCool"
                    selectable
                    tabindex="0"
                    role="button"
                    onClick={() => navigate('/admin/user-management')}
                  >
                    <Stack
                      py={1}
                      px={1}
                    >
                      <Type
                        variant="p"
                      >
                        User Management
                      </Type>
                    </Stack>
                  </Paper>
                  <Paper
                    round="extra"
                    variant="transparent"
                    variantHover="neutralCool"
                    selectable
                    tabindex="0"
                    role="button"
                    onClick={() => navigate('/admin/budgeting')}
                  >
                    <Stack
                      py={1}
                      px={1}
                    >
                      <Type
                        variant="p"
                      >
                        Manager Budgets
                      </Type>
                    </Stack>
                  </Paper>
                  <Paper
                    round="extra"
                    variant="transparent"
                    variantHover="neutralCool"
                    selectable
                    tabindex="0"
                    role="button"
                    onClick={() => navigate('/admin/statements')}
                  >
                    <Stack
                      py={1}
                      px={1}
                    >
                      <Type
                        variant="p"
                      >
                        Reports
                      </Type>
                    </Stack>
                  </Paper>
                  <Paper
                    round="extra"
                    variant="transparent"
                    variantHover="neutralCool"
                    selectable
                    tabindex="0"
                    role="button"
                    onClick={() => navigate('/advanced-stats')}
                  >
                    <Stack
                      py={1}
                      px={1}
                    >
                      <Type
                        variant="p"
                      >
                        Advanced Stats
                      </Type>
                    </Stack>
                  </Paper>
                </>
              )}
              {user.is_manager && (
                <Paper
                  round="extra"
                  variant="transparent"
                  variantHover="neutralCool"
                  selectable
                  tabindex="0"
                  role="button"
                  onClick={() => navigate('/my-team')}
                >
                  <Stack
                    py={1}
                    px={1}
                  >
                    <Type
                      variant="p"
                    >
                      My Team
                    </Type>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
      <Box
        justifySelf="center"
      >
        <TextButton
          color="error"
          onClick={handleLogout}
        >
          Sign Out
        </TextButton>
      </Box>
    </Stack>
  )
}

const UserNav = () => {
  const avatarRef = useRef()
  const roles = useRoles()
  const user = useSelector(({ global }) => global.user)
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const { xs, md } = useBreakpoints()

  const handleClick = e => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    setOpen(previousOpen => !previousOpen)
  }

  const handleOpen = () => {
    if (!open) {
      setOpen(true)
      setAnchorEl(avatarRef.current)
    }
  }

  const handleClose = () => {
    if (open) {
      setAnchorEl(null)
      setOpen(false)
    }
  }

  const canBeOpen = open && Boolean(anchorEl)
  const id = canBeOpen ? 'transition-popper' : undefined
  const dimensions = roles.hasAnyRole ? {
    xx: 40,
    xs: 24,
    md: 40,
  } : {
    xs: 40,
    md: 50,
  }

  return (
    <span>
      <Stack
        ref={avatarRef}
        sx={theme => ({
          display: 'inline-grid',
          gridAutoFlow: 'column',
          alignItems: 'center',
          p: roles.hasAnyRole && xs ? .5 : 0,
          borderRadius: '50px',
          cursor: 'pointer',
          background: alpha(theme.palette.primary.main, .1),
        })}
        tabindex="0"
        onClick={handleClick}
      >
        <Avatar
          src={user?.profile_image_url}
          sx={{
            width: dimensions,
            height: dimensions,
          }}
        />
        {roles.hasAnyRole && xs && (
          <Type
            variant="bodySmallBold"
            color="primaryHue"
            px={1}
          >
            {roles.userRoleNames.map((o, idx) => (
              <div
                key={`role${o}`}
                style={{ lineHeight: 1 }}
              >
                {o}{roles.userRoleNames.length > 1 && idx !== roles.userRoleNames.length - 1 ? ' /' : ''}
              </div>
            ))}
          </Type>
        )}
      </Stack>
      {!md && (
        <SwipeableDrawer
          anchor="right"
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
        >
          <Stack>
            <Stack
              sx={theme => theme.internalMixins.columns({ gap: 2, justify: 'space-between' })}
              pl={2}
            >
              <Type
                variant="title"
              >
                Menu
              </Type>
              <Box
                tabindex="0"
                role="button"
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  cursor: 'pointer',
                }}
                onClick={handleClose}
              >
                <Icon
                  icon="Close"
                />
              </Box>
            </Stack>
            <NavContent />
          </Stack>
        </SwipeableDrawer>
      )}
      {md && (
        <ClickAwayListener 
          onClickAway={handleClose}
        >
          <Popper 
            id={id} 
            open={open} 
            anchorEl={anchorEl} 
            transition
            sx={{
              zIndex: 'drawer',
            }}
            placement="bottom-end"
            modifiers={[
              {
                name: 'arrow',
                enabled: true,
              },
            ]}
          >
            {({ TransitionProps }) => (
              <Fade 
                {...TransitionProps} 
                timeout={350}
              >
                <div>
                  <Paper
                    round="doubleExtra"
                    sx={{
                      filter: 'drop-shadow(0px 7px 15px rgba(42, 115, 156, 0.25))',
                      backdropFilter: 'blur(7.5px)',
                    }}
                  >
                    <NavContent />
                  </Paper>
                </div>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </span>
  )
}

export default UserNav